import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { PropertyListings } from '@/types/PropertyListings';
import { Clock, Eye, Heart, Mail, TrendingUp, TrendingDown, Users, Calendar, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '@/lib/axiosInstance';
import { toast } from 'sonner';
import Helper from '@/lib/helper';

interface _Stats {
    listing: PropertyListings;
}

type TrendPeriod = 'week' | 'month' | '3months' | '6months' | '12months';

interface TrendData {
    views: number;
    viewsTrend: number;
    favorites: number;
    favoritesTrend: number;
    enquiries: number;
    enquiriesTrend: number;
    viewsCurrentPeriod: number;
    uniqueViewers: number;
    avgTimeSpent: number;
    conversionRate: string;
}

interface ChartDataPoint {
    date: string;
    views: number;
    favorites: number;
    enquiries: number;
}

const Stats: React.FC<_Stats> = ({ listing: initialListing }) => {
    const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('week');
    const [trendData, setTrendData] = useState<TrendData>({
        views: 0,
        viewsTrend: 0,
        favorites: 0,
        favoritesTrend: 0,
        enquiries: 0,
        enquiriesTrend: 0,
        viewsCurrentPeriod: 0,
        uniqueViewers: 0,
        avgTimeSpent: 0,
        conversionRate: '0%',
    });
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
    const [loading, setLoading] = useState(true);

    const periodLabels: Record<TrendPeriod, string> = {
        week: 'Last Week',
        month: 'Last Month',
        '3months': 'Last 3 Months',
        '6months': 'Last 6 Months',
        '12months': 'Last 12 Months',
    };

    useEffect(() => {
        fetchTrendData();
    }, [trendPeriod, initialListing.id]);

    const fetchTrendData = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/listings/${initialListing.id}/stats`, {
                params: { period: trendPeriod }
            });

            if (!response.data.status) {
                throw new Error(response.data.error);
            }

            const listing = response.data.listing;
            const stats = listing.stats || {};
            
            setTrendData({
                views: stats.views || 0,
                viewsTrend: stats.viewsTrend || 0,
                favorites: stats.favorites || 0,
                favoritesTrend: stats.favoritesTrend || 0,
                enquiries: stats.enquiries || 0,
                enquiriesTrend: stats.inquiriesTrend || 0,
                viewsCurrentPeriod: stats.viewsCurrentPeriod || 0,
                uniqueViewers: listing.uniqueViewers || 0,
                avgTimeSpent: listing.avgTime?.avgTimeSpent || 0,
                conversionRate: stats.conversionRate || '0%',
            });
            
            if (response.data.chartData) {  
                setChartData(response.data.chartData);
            }
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setLoading(false);
        }
    };

    const renderTrendBadge = (trend: number) => {
        const isPositive = trend >= 0;
        const TrendIcon = isPositive ? TrendingUp : TrendingDown;
        
        return (
            <Badge
                variant="secondary"
                className={`mt-2 text-xs ${
                    isPositive
                        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                        : 'bg-red-500/10 text-red-600 dark:text-red-400'
                }`}
            >
                <TrendIcon size={10} className="mr-1" />
                {Math.abs(trend).toFixed(1)}%
            </Badge>
        );
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <div key={index} className="flex items-center gap-2 text-xs">
                            <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-slate-600 dark:text-slate-400">{entry.name}:</span>
                            <span className="font-semibold text-slate-900 dark:text-slate-100">{entry.value}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Performance Analytics</h2>
                <Select value={trendPeriod} onValueChange={(value) => setTrendPeriod(value as TrendPeriod)}>
                    <SelectTrigger className="w-[180px]">
                        <Calendar size={16} className="mr-2" />
                        <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="week">Last Week</SelectItem>
                        <SelectItem value="month">Last Month</SelectItem>
                        <SelectItem value="3months">Last 3 Months</SelectItem>
                        <SelectItem value="6months">Last 6 Months</SelectItem>
                        <SelectItem value="12months">Last 12 Months</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-border">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Total Views
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-end justify-between">
                            <div>
                                <p className="text-2xl font-semibold text-foreground">
                                    {loading ? '...' : trendData.views.toLocaleString()}
                                </p>
                                {!loading && renderTrendBadge(trendData.viewsTrend)}
                            </div>
                            <Eye className="text-muted-foreground" size={24} />
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Favorites
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-end justify-between">
                            <div>
                                <p className="text-2xl font-semibold text-foreground">
                                    {loading ? '...' : trendData.favorites.toLocaleString()}
                                </p>
                                {!loading && renderTrendBadge(trendData.favoritesTrend)}
                            </div>
                            <Heart className="text-muted-foreground" size={24} />
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Enquiries
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-end justify-between">
                            <div>
                                <p className="text-2xl font-semibold text-foreground">
                                    {loading ? '...' : trendData.enquiries.toLocaleString()}
                                </p>
                                {!loading && renderTrendBadge(trendData.enquiriesTrend)}
                            </div>
                            <Mail className="text-muted-foreground" size={24} />
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card className="border-border">
                <CardHeader className="pb-4 border-b border-border/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CardTitle className="text-base font-semibold">Performance Trends</CardTitle>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {periodLabels[trendPeriod]}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    {loading ? (
                        <div className="h-80 flex items-center justify-center">
                            <div className="flex flex-col items-center gap-3">
                                <Activity className="animate-pulse text-blue-500" size={32} />
                                <p className="text-sm text-muted-foreground">Loading chart data...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    data={chartData}
                                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorFavorites" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorEnquiries" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                                    <XAxis 
                                        dataKey="date" 
                                        stroke="hsl(var(--muted-foreground))"
                                        style={{ fontSize: '12px' }}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        stroke="hsl(var(--muted-foreground))"
                                        style={{ fontSize: '12px' }}
                                        tickLine={false}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend 
                                        wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                                        iconType="circle"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="views"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        fill="url(#colorViews)"
                                        name="Views"
                                        animationDuration={1500}
                                        animationBegin={0}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="favorites"
                                        stroke="#ec4899"
                                        strokeWidth={2}
                                        fill="url(#colorFavorites)"
                                        name="Favorites"
                                        animationDuration={1500}
                                        animationBegin={200}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="enquiries"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        fill="url(#colorEnquiries)"
                                        name="Enquiries"
                                        animationDuration={1500}
                                        animationBegin={400}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-border">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-base font-medium">Engagement Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md border border-border/50">
                            <div className="flex items-center gap-3">
                                <Users className="text-muted-foreground" size={16} />
                                <span className="text-xs font-medium text-foreground">Unique Visitors</span>
                            </div>
                            <span className="text-sm font-semibold text-foreground">
                                {loading ? '...' : trendData.uniqueViewers.toLocaleString()}
                            </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md border border-border/50">
                            <div className="flex items-center gap-3">
                                <Clock className="text-muted-foreground" size={16} />
                                <span className="text-xs font-medium text-foreground">Avg. Time on Page</span>
                            </div>
                            <span className="text-sm font-semibold text-foreground">
                                {loading ? '...' : `${Number(trendData.avgTimeSpent).toFixed(2)}s`}
                            </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md border border-border/50">
                            <div className="flex items-center gap-3">
                                <TrendingUp className="text-muted-foreground" size={16} />
                                <span className="text-xs font-medium text-foreground">Conversion Rate</span>
                            </div>
                            <span className="text-sm font-semibold text-foreground">
                                {loading ? '...' : trendData.conversionRate}
                            </span>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-base font-medium">
                            {periodLabels[trendPeriod]} Performance
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-muted-foreground">
                                        Views in period
                                    </span>
                                    <span className="text-sm font-semibold text-foreground">
                                        {loading ? '...' : trendData.viewsCurrentPeriod.toLocaleString()}
                                    </span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-1000 ease-out"
                                        style={{
                                            width: `${Math.min(100, Math.max(5, trendData.viewsCurrentPeriod > 0 ? 100 : 0))}%`
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="pt-2 border-t border-border/50 space-y-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Favorites in period</span>
                                    <span className="font-semibold text-foreground">
                                        {loading ? '...' : trendData.favorites.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Enquiries in period</span>
                                    <span className="font-semibold text-foreground">
                                        {loading ? '...' : trendData.enquiries.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-4">
                                {trendData.viewsTrend >= 0
                                    ? `Your listing is up ${Math.abs(trendData.viewsTrend).toFixed(1)}% compared to the previous period. Keep the momentum going!`
                                    : `Views are down ${Math.abs(trendData.viewsTrend).toFixed(1)}% compared to the previous period. Consider updating your listing to boost visibility.`}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Stats;